import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import {
  updateRecipientListFilter,
  resetRecipientList,
} from '@feature/dashboard/recipients/store/recipient-list.actions';
import { selectRecipientList } from '@feature/dashboard/recipients/store/recipient-list.selector';
import { RecipientListInterface } from '@feature/dashboard/recipients/types/recipient-list';
import {
  defaultRecipientListFilter,
  RecipientListFilter,
} from '@feature/dashboard/recipients/types/recipient-list-filter';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sender-recipient-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './sender-recipient-list.component.html',
  styleUrl: './sender-recipient-list.component.scss',
})
export class SenderRecipientListComponent implements OnInit, OnDestroy {
  @Input() id!: string;
  @Input() isSuperAdmin!: boolean;
  public readonly attrConstant = ATTRIBUTES_CONSTANT.recipients;
  senderId!: number;
  partnerId?: number;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'id',
    'bene_first_name',
    'benificiary_country',
    'bene_mobile_number',
    'created_at',
  ];
  recipientList?: RecipientListInterface[];
  dataSource!: MatTableDataSource<RecipientListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    search: string;
  }>();
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private excelExportService: ExcelExportService
  ) {}

  ngOnInit(): void {
    if (this.isSuperAdmin) {
      this.senderId = +this.id.split('-')[0];
      this.partnerId = +this.id.split('-')[1];
    } else {
      this.senderId = +this.id;
    }
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        this.dispatchFilter({ count: 10, page: 0, ...res });
      });
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.search.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectRecipientList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.recipientList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({
      ...defaultRecipientListFilter,
      partner_id: this.partnerId ? [this.partnerId] : null,
      sender_id: [this.senderId],
    });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(recipientListFilter?: RecipientListFilter): void {
    this.store.dispatch(
      updateRecipientListFilter({
        filter: {
          ...recipientListFilter,
          partner_id: this.partnerId ? [this.partnerId] : null,
          sender_id: [this.senderId],
        },
      })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(this.recipientList!, 'recipients');
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetRecipientList());
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
